import type { ComponentNode } from '@deckspace/types';
import { getVisualization } from '@deckspace/catalog';

/**
 * Derive a CSS Grid template string for a Row/Column container's children.
 *
 * Layout decisions live on the parent (Grafana model). Precedence:
 *
 *   1. `parent.props.tracks` — if an array whose length matches `children.length`,
 *      entries are joined verbatim (server-side validated). Highest precedence.
 *   2. Per-instance `child.props.style.height` (number) — emits `<N>px` as the
 *      child's track. Symmetric across axes for predictability. Token values
 *      (`'full'`, `'auto'`) are display-semantic, not pixel sizing, and fall
 *      through to catalog defaults.
 *   3. Catalog `defaultHeight` (Notion database-block pattern, ADR-0004) on the
 *      row axis — emits `<N>px` fixed track. Used for DataTable, Kanban, List.
 *      Does not apply on col axis (row-axis sizing concern only).
 *   4. Catalog `defaultMinHeight` on the row axis — emits `minmax(Npx, 1fr)`
 *      (flex behavior for charts; prevents grid-row collapse, absorbs extra
 *      parent space).
 *   5. Layout-category fallbacks: `expandsVertically: true` on row axis
 *      (Tabs, Accordion) → `minmax(0, 1fr)`; other layout children → `auto`.
 *   6. Axis fallback: row axis → `auto` (content-sized so Callouts/Text don't
 *      squeeze charts off the page); col axis → `minmax(0, 1fr)` (children
 *      share width evenly).
 *
 * Missing children produce `auto` (defensive — should not happen given
 * validateSurfaceComponents). Unknown component types follow the axis fallback.
 */
export function deriveTracks(
  parent: ComponentNode,
  nodeMap: Map<string, ComponentNode>,
  axis: 'col' | 'row'
): string {
  const children = parent.children ?? [];
  if (children.length === 0) return '';

  const explicit = (parent.props as { tracks?: unknown } | undefined)?.tracks;
  if (Array.isArray(explicit) && explicit.length === children.length) {
    return explicit.join(' ');
  }

  return children
    .map((childId) => {
      const child = nodeMap.get(childId);
      if (!child) return 'auto';

      // Per-instance numeric style.height override — wins over catalog defaults
      // (but not over parent.props.tracks, which is handled above).
      const styleHeight = (child.props as { style?: { height?: unknown } } | undefined)?.style
        ?.height;
      if (typeof styleHeight === 'number') return `${styleHeight}px`;

      const def = getVisualization(child.type);
      if (def?.category === 'layout') {
        return def.expandsVertically && axis === 'row' ? 'minmax(0, 1fr)' : 'auto';
      }

      // Catalog defaultHeight: row-axis fixed cell (Notion database-block model,
      // ADR-0004). Used by DataTable, Kanban, List.
      const fixedH = def?.defaultHeight ?? 0;
      if (axis === 'row' && fixedH > 0) return `${fixedH}px`;

      // Catalog defaultMinHeight: row-axis flex sizing for charts.
      const minH = def?.defaultMinHeight ?? 0;
      if (axis === 'row' && minH > 0) return `minmax(${minH}px, 1fr)`;

      // Axis fallback. Row: content-sized. Col: share width evenly.
      return axis === 'row' ? 'auto' : 'minmax(0, 1fr)';
    })
    .join(' ');
}
